/**
 * Incremental parser for a JSON array streamed chunk by chunk over HTTP, e.g.
 * `[{"id":"a","message":"..."},{"id":"b","message":"..."}]` arriving as several
 * `fetch()` `ReadableStream` reads rather than one complete body.
 *
 * `fetchBaseQuery` (and RTK Query generally) awaits the whole response body before
 * handing back JSON, so it can't surface progress as a streamed array fills in — this
 * is what makes incremental parsing here necessary instead of just `JSON.parse`-ing
 * once at the end (see migrationStreamApi.ts).
 *
 * The backend has this drafted as a real JSON array (not NDJSON), so this can't just
 * split on newlines — it tracks bracket/string state to find each complete top-level
 * element as it completes, without needing the array to be fully buffered first.
 *
 * Assumes every array element is itself an object or array (true for `MigratedItem`,
 * which is always `{ id, message }`) — a bare top-level primitive/string element
 * would not be detected as "complete" by the brace/bracket depth tracking below.
 */
export class StreamingJsonArrayParser<T> {
    private buffer = "";
    private started = false;
    private elementStart = -1;
    private depth = 0;
    private inString = false;
    private escapeNext = false;
    private finished = false;

    /**
     * Feed the next chunk of raw response text in. Returns any array elements that
     * became complete as a result, in order.
     */
    push(chunk: string): T[] {
        if (this.finished) {
            return [];
        }

        this.buffer += chunk;

        const results: T[] = [];
        let i = 0;

        while (i < this.buffer.length) {
            const char = this.buffer[i];

            if (!this.started) {
                if (char === "[") {
                    this.started = true;
                }
                i++;
                continue;
            }

            if (this.elementStart === -1) {
                if (char === "," || this.isWhitespace(char)) {
                    i++;
                    continue;
                }
                if (char === "]") {
                    this.finished = true;
                    this.buffer = "";
                    return results;
                }
                this.elementStart = i;
                this.depth = 0;
                this.inString = false;
                this.escapeNext = false;
                // Fall through so this same character is processed below.
            }

            if (this.inString) {
                if (this.escapeNext) {
                    this.escapeNext = false;
                } else if (char === "\\") {
                    this.escapeNext = true;
                } else if (char === "\"") {
                    this.inString = false;
                }
                i++;
                continue;
            }

            if (char === "\"") {
                this.inString = true;
                i++;
                continue;
            }

            if (char === "{" || char === "[") {
                this.depth++;
                i++;
                continue;
            }

            if (char === "}" || char === "]") {
                this.depth--;
                i++;
                if (this.depth === 0) {
                    const elementText = this.buffer.slice(this.elementStart, i);
                    results.push(JSON.parse(elementText) as T);
                    this.buffer = this.buffer.slice(i);
                    i = 0;
                    this.elementStart = -1;
                }
                continue;
            }

            i++;
        }

        return results;
    }

    /** Whether the closing `]` of the outer array has been seen. */
    isFinished(): boolean {
        return this.finished;
    }

    private isWhitespace(char: string): boolean {
        return char === " " || char === "\n" || char === "\r" || char === "\t";
    }
}
